import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { COLORS } from 'app/shared/colors';
import { BackendService } from 'app/shared/services/backend.service';
import { UserService } from 'app/shared/services/user.service';
import { Subject } from 'rxjs';

export enum UserNotificationType {
  // The user gets a notification that the founder has removed him/her from the team
  USER_REMOVED = 'userRemoved',
  // Members of the team get a notification when team is deleted
  TEAM_DELETED = 'deleteTeam',
  // The founder gets a notification that a user left the team
  USER_LEFT_TEAM = 'leaveTeam',
  // The founder cancels an invitation and the user gets a notification
  INVITATION_CANCEL = 'cancelInvitation',
  // The founder gets a notification that a user has applied for the team
  APPLICATION_NEW = 'applicationNew',
  // The user gets a notification that the founder declined the application
  APPLICATION_CANCEL = 'applicationCancel',
  // The user gets a notification that the founder accepted the application
  APPLICATION_ACCEPT = 'applicationAccept',
  // Every user gets a notification if his team is weekly winner team
  WEEKLY_WINNER = 'weeklyWinner',
}

export interface UserNotification {
  _id: string;
  type: UserNotificationType;
  userID: string;
  data?: {
    userName?: string;
    teamName?: string;
    userID?: string;
    challengeID?: string;
    teamID?: string;
  };
}

export interface UserNotificationOptions {
  message: string;
  visible: boolean;
  duration: number;
  dismissible: boolean;
  color: string;
  buttons: UserNotificationButton[];
}

// Button that can be displayed within user notification
export interface UserNotificationButton {
  label: string;
  style: string;
  action: UserNotificationAction;
}

// Actions available in user notification action button
export interface UserNotificationAction {
  type: UserNotificationActionType;
  teamID?: string;
  userID?: string;
  challengeID?: string;
}

export enum UserNotificationActionType {
  ACCEPT_APPLICATION = 'acceptApplication',
  REJECT_APPLICATION = 'rejectApplication',
  MANAGE_TEAM = 'manageTeam',
  SELECT_TEAM = 'selectTeam',
}

@Injectable()
export class NotificationService implements OnDestroy {
  private notificationQueue = [];

  private timeout;

  private _notificationOptions = new Subject<UserNotificationOptions>();

  notificationOptionsObservable = this._notificationOptions.asObservable();

  private notificationWorker = null;

  private currentUser: string = null;

  constructor(private translate: TranslateService, private userService: UserService, private backendService: BackendService) {
    this.userService.currentUser.subscribe((user) => {
      if (!user || user._id !== this.currentUser) {
        clearInterval(this.notificationWorker);
      }

      if (!user || this.currentUser === user._id) {
        return;
      }
      this.currentUser = user._id;
      this.getNotificationsForUser(user._id);
      // Fetch new notifications every 2 minutes, make sure interval is cleared when user changes and in OnDestroy
      this.notificationWorker = setInterval(() => this.getNotificationsForUser(user._id), 120000);
    });
  }

  private async getNotificationsForUser(userID: string) {
    try {
      const notifications = await this.backendService.get<UserNotification[]>(`notification/user/${userID}`);

      if (!notifications) {
        return;
      }

      for (const notification of notifications) {
        this.displayNotification(notification);
      }

      // Dismiss notifications so they do not appear again
      if (notifications.length > 0) {
        this.dismissNotifications(
          notifications[0].userID,
          notifications.map((notification) => notification._id),
        ).then();
      }
    } catch (error) {}
  }

  public async dismissNotifications(userID: string, notificationIds: string[]) {
    await this.backendService.post(`notification/user/${userID}/dismiss`, { notificationIds: notificationIds });
  }

  /**
   * Adds a snackbar message to queue
   * @param {string} message         Message that should be displayed
   * @param {number} [duration=3000] Duration of message display (optional)
   * @param buttons Buttons that should be displayed in notification
   */
  public add(message: string, duration = 3000, buttons: UserNotificationButton[] = []) {
    const snackbarOptions = {
      message: message,
      visible: false,
      duration: isNaN(duration) || duration <= 0 ? 5000 : duration,
      color: COLORS.main.white.hex,
      buttons,
    };

    this.notificationQueue.push(snackbarOptions);

    if (this.notificationQueue.length === 1) {
      this.openNextNotification();
    }
  }

  /**
   * Display next item in queue
   */
  private openNextNotification() {
    if (this.notificationQueue.length > 0) {
      this.notificationQueue[0]['visible'] = true;

      this._notificationOptions.next(this.notificationQueue[0]);
    }
  }

  /**
   * Dismiss message and trigger next item in queue
   */
  public dismiss() {
    this.notificationQueue[0]['visible'] = false;

    this._notificationOptions.next(this.notificationQueue[0]);

    // Show next message after old one is hidden
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      // Remove first element of queue
      this.notificationQueue.shift();

      this.openNextNotification();
    }, 250);
  }

  public async displayNotification(notification: UserNotification) {
    switch (notification.type) {
      // Displayed to the founder, when another user leaves the team
      case UserNotificationType.USER_LEFT_TEAM: {
        this.add(this.translate.instant('NOTIFICATION.text.userLeftTeam'), 20000, [
          {
            label: this.translate.instant('NOTIFICATION.action.manageTeam'),
            style: 'primary',
            action: { type: UserNotificationActionType.MANAGE_TEAM },
          },
        ]);
        break;
      }

      // Displayed for founder, when a user applies for the team
      case UserNotificationType.APPLICATION_NEW: {
        this.add(this.translate.instant('NOTIFICATION.text.applicationNew', { user: notification.data.userName }), 20000, [
          {
            label: this.translate.instant('NOTIFICATION.action.acceptApplication'),
            style: 'primary',
            action: {
              type: UserNotificationActionType.ACCEPT_APPLICATION,
              userID: notification.data.userID,
              teamID: notification.data.teamID,
              challengeID: notification.data.challengeID,
            },
          },
          {
            label: this.translate.instant('NOTIFICATION.action.denyApplication'),
            style: 'error',
            action: {
              type: UserNotificationActionType.REJECT_APPLICATION,
              userID: notification.data.userID,
              teamID: notification.data.teamID,
              challengeID: notification.data.challengeID,
            },
          },
        ]);
        break;
      }

      case UserNotificationType.APPLICATION_CANCEL: {
        this.add(this.translate.instant('NOTIFICATION.text.applicationCancelled', { team: notification.data.teamName }), 20000, [
          {
            label: this.translate.instant('NOTIFICATION.action.selectTeam'),
            style: 'primary',
            action: {
              type: UserNotificationActionType.SELECT_TEAM,
              challengeID: notification.data.challengeID,
            },
          },
        ]);
        break;
      }

      case UserNotificationType.APPLICATION_ACCEPT: {
        this.add(this.translate.instant('NOTIFICATION.text.applicationAccepted', { team: notification.data.teamName }), 20000);
        break;
      }

      case UserNotificationType.TEAM_DELETED: {
        this.add(this.translate.instant('NOTIFICATION.text.teamDeleted', { team: notification.data.teamName }), 20000, [
          {
            label: this.translate.instant('NOTIFICATION.action.selectTeam'),
            style: 'primary',
            action: {
              type: UserNotificationActionType.SELECT_TEAM,
              challengeID: notification.data.challengeID,
            },
          },
        ]);
        break;
      }

      case UserNotificationType.USER_REMOVED: {
        this.add(this.translate.instant('NOTIFICATION.text.userRemoved', { team: notification.data.teamName }), 20000, [
          {
            label: this.translate.instant('NOTIFICATION.action.selectTeam'),
            style: 'primary',
            action: {
              type: UserNotificationActionType.SELECT_TEAM,
              challengeID: notification.data.challengeID,
            },
          },
        ]);
        break;
      }

      case UserNotificationType.INVITATION_CANCEL: {
        this.add(this.translate.instant('NOTIFICATION.text.invitationCancelled', { team: notification.data.teamName }), 20000, [
          {
            label: this.translate.instant('NOTIFICATION.action.selectTeam'),
            style: 'primary',
            action: {
              type: UserNotificationActionType.SELECT_TEAM,
              challengeID: notification.data.challengeID,
            },
          },
        ]);
        break;
      }

      case UserNotificationType.WEEKLY_WINNER: {
        this.add(this.translate.instant('NOTIFICATION.text.weeklyWinner', { team: notification.data.teamName }), 20000);
        break;
      }

      default: {
        break;
      }
    }
  }

  /**
   * On destroy cancel fetch notification job
   */
  public ngOnDestroy() {
    if (this.notificationWorker) {
      clearInterval(this.notificationWorker);
    }
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/theme/components/user-notification/notification.service.ts